import { XMarkIcon } from "@heroicons/react/24/solid";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    confirmFileUpload,
    getPresignedBucketUrl,
    getTestsByQuery,
    getTestsInProgress,
    pollFileValidation,
    uploadFileToS3,
} from "../../api/open-test";
import searchIcon from "../../assets/icons/Icon search.svg";
import { ReactComponent as UploadIcon } from "../../assets/icons/upload-icon.svg";

import { twMerge } from "tailwind-merge";
import Loader from "../../components/AppLoader";
import BottomNavigationButtons from "../../components/BottomNavigationButtons";
import DatePickerButton from "../../components/DatePickerButton";
import FileUploadProcessingDialog from "../../components/FileUploadProcessingDialog";
import useAlert from "../../hooks/useAlert";
import { TestSearchResult } from "../../types";
import { convertSearchResultsFromAPI, createFileDigest } from "../../utils";
import TestSearchResultRow from "./TestSearchResultRow";

export default function OpenTest() {
    const [file, setFile] = useState<File | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] =
        useState<Array<TestSearchResult> | null>(null);
    const [searchError, setSearchError] = useState<string | null>(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const [selectedTest, setSelectedTest] = useState<number>();
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [selectedButton, setSelectedButton] = useState<
        "show-tests-in-progress" | "upload-new-test" | null
    >(null);
    const [uploadProcessing, setUploadProcessing] = useState(false);
    const [uploadStageDescription, setUploadStageDescription] =
        useState<string>("");
    const [uploadDialogError, setUploadDialogError] = useState<string | null>(
        null
    );
    const [fileDigest, setFileDigest] = useState<string | null>(null);
    const { show: showAlert } = useAlert();

    const navigate = useNavigate();

    const searchFormRef = useRef<HTMLFormElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);

    const mainButtonClass = twMerge(
        "btn-outline-primary border-blue-medium2 text-blue-medium2 h-10 !font-semibold !text-base xl:!text-lg"
    );
    const selectedButtonClass =
        "bg-blue-dark2/15 text-blue-dark2 border-blue-dark2";

    const resetFile = () => {
        setFile(null);
        fileInputRef.current!.value = "";
        setUploadProcessing(false);
        setUploadStageDescription("");
        setUploadDialogError(null);
        setFileDigest(null);
    };
    const resetUploadProcessingDialog = () => {
        setUploadProcessing(false);
        setUploadStageDescription("");
        setUploadDialogError(null);
    }

    const handleNewUploadWorkflow = async (file: File) => {
        setUploadProcessing(true);
        setUploadDialogError(null);

        try {
            const digest = await createFileDigest(file);

            const presignedResult = await getPresignedBucketUrl({
                filename: file.name,
                digest,
            });

            if (presignedResult.error || !presignedResult.data) {
                throw new Error(
                    presignedResult.error || "Failed to get presigned URL"
                );
            }

            const uploadResult = await uploadFileToS3({
                file,
                presignedData: presignedResult.data,
            });

            if (!uploadResult.success) {
                throw new Error(uploadResult.error || "Failed to upload file");
            }

            const validationResult = await pollFileValidation({ digest });

            if (validationResult.error || !validationResult.data) {
                throw new Error(
                    validationResult.error || "File validation failed"
                );
            }

            const validation = validationResult.data;

            if (validation.code === "error") {
                throw new Error(validation.message || "File validation error");
            }

            if (
                validation.code === "already_exists" &&
                validation.results?.[0]
            ) {
                resetUploadProcessingDialog();
                resetFile();
                showAlert({
                    title: "The selected file already exists",
                });
                setSearchResults(
                    convertSearchResultsFromAPI(validation.results)
                );
                setSelectedTest(validation.results[0].test_id);
                return;
            }

            if (validation.code === "clean") {
                setFile(file);
                setFileDigest(digest);
                setSelectedButton("upload-new-test");
                resetUploadProcessingDialog();
            }
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : "Upload failed";
            setUploadStageDescription(errorMessage);
            setUploadDialogError(errorMessage);
            setSearchResults(null);
        }
    };

    const handleCloseUploadDialog = () => {
        resetUploadProcessingDialog();
        resetFile();
    };

    const handleFileUpload = async (
        event: React.ChangeEvent<HTMLInputElement> & {
            target: { files: FileList | null };
        }
    ) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedTest(undefined);
            setSearchResults(null);

            await handleNewUploadWorkflow(file);
        }
    };

    const handleSearch = async () => {
        setSearchLoading(true);

        const searchQueryVal = searchInputRef.current?.value || "";
        const searchResultsFromAPI = await getTestsByQuery({
            query: searchQueryVal,
        });

        if (searchResultsFromAPI) {
            setSearchError(null);
            setSearchResults(convertSearchResultsFromAPI(searchResultsFromAPI));
        } else {
            setSearchError("Failed to get test results");
        }
        setUploadError(null);
        resetFile();
        setSearchLoading(false);
    };

    const handleOpenInProgress = async () => {
        setSearchLoading(true);
        const searchResultsFromAPI = await getTestsInProgress();
        setSelectedButton("show-tests-in-progress");

        if (searchResultsFromAPI) {
            setSearchError(null);
            setSearchResults(
                searchResultsFromAPI.map((result) => ({
                    firstName: result.first_name,
                    lastName: result.last_name,
                    personId: result.person_id,
                    status: result.test_status,
                    testDate: new Date(result.test_date),
                    testId: result.test_id,
                }))
            );
        } else {
            setSearchError("Failed to get test results");
        }
        setSearchLoading(false);
    };

    // MARK: Render
    return (
        <div className="OpenTestPage isWhitePage mt-[60px] flex flex-col pb-bottomPanel">
            <div className="flex flex-col gap-7 xl:gap-10 pl-32 w-[876px] h-full">
                <div className="flex gap-4 items-center">
                    <button
                        onClick={handleOpenInProgress}
                        className={twMerge(
                            mainButtonClass,
                            selectedButton === "show-tests-in-progress" &&
                                selectedButtonClass
                        )}
                    >
                        Show tests in progress
                    </button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept=".csv, .xlsx, .xls"
                        onChange={handleFileUpload}
                        key={file ? file.name : ""} // Add key to force re-render
                    />
                    <button
                        disabled={uploadProcessing}
                        onClick={() => {
                            if (fileInputRef.current) {
                                fileInputRef.current.click();
                            }
                        }}
                        className={twMerge(
                            mainButtonClass,
                            selectedButton === "upload-new-test" &&
                                selectedButtonClass,
                            uploadProcessing && "opacity-50 cursor-not-allowed"
                        )}
                    >
                        <UploadIcon className="[&_*]:stroke-current" />
                        Upload a new test
                    </button>
                    {uploadError ? (
                        <span className="text-red-error max-w-[400px] overflow-y-auto">
                            {uploadError}
                        </span>
                    ) : file ? (
                        <div className="flex gap-2">
                            <span>{file.name}</span>
                            <XMarkIcon
                                className="h-6 w-6 cursor-pointer"
                                onClick={resetFile}
                            />
                        </div>
                    ) : null}
                </div>

                <form
                    ref={searchFormRef}
                    onSubmit={(ev) => {
                        ev.preventDefault();
                        setSelectedButton(null);
                        handleSearch();
                    }}
                    className="relative flex bg-blue-light"
                >
                    <input
                        type="search"
                        required
                        ref={searchInputRef}
                        value={searchQuery}
                        className="search-bar"
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Enter the patient's first and last name or ID number"
                    />
                    <DatePickerButton
                        classes={{ root: "pe-1" }}
                        onChange={(date: any) => {
                            if (date) {
                                const dateStr =
                                    date.toLocaleDateString("he-IL");
                                searchInputRef.current!.value = dateStr;
                                setSearchQuery(dateStr);
                                handleSearch();
                            }
                        }}
                    />
                    <button
                        disabled={!searchQuery}
                        className="btn-primary w-16 xl:w-20 disabled:bg-blue-disabled disabled:border-blue-disabled"
                        type="submit"
                    >
                        <img
                            src={searchIcon}
                            alt="Search icon"
                            className="h-6 w-6"
                        />
                    </button>
                </form>

                {/*  MARK: Search results
                 */}
                <div className="overflow-y-auto">
                    {searchLoading ? (
                        <Loader />
                    ) : searchError ? (
                        <span className="text-red-error">{searchError}</span>
                    ) : searchResults ? (
                        searchResults.length > 0 ? (
                            <table className="w-full">
                                <thead className="text-center bg-blue-dark text-white border-b-2 border-black font-bold h-9 xl:h-12 text-base">
                                    <tr>
                                        <th></th>
                                        <th>Patient Name</th>
                                        <th>Patient ID</th>
                                        <th>Test Date</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody className="text-center">
                                    {searchResults.map((result, index) => (
                                        <TestSearchResultRow
                                            key={result.testId}
                                            result={result}
                                            index={index}
                                            isSelected={
                                                selectedTest === result.testId
                                            }
                                            onSelect={(testId) => {
                                                setSelectedTest(testId);
                                                resetFile();
                                            }}
                                            warning={
                                                file
                                                    ? "Selecting a test from the table will remove the file you've uploaded. Are you sure you want to proceed?"
                                                    : ""
                                            }
                                        />
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div>
                                <p>No results found</p>
                            </div>
                        )
                    ) : null}
                </div>
            </div>
            <BottomNavigationButtons
                isFixed
                onCancelClick={() => {
                    resetFile();
                    setSearchResults(null);
                    setSelectedTest(undefined);
                    setSelectedButton(null);
                }}
                isConfirmDisabled={!file && !selectedTest}
                isConfirmLoading={confirmLoading}
                onConfirmClick={async () => {
                    let testIdToUse = selectedTest;
                    if (file) {
                        if (!fileDigest) {
                            alert(
                                "File digest is missing. Please re-upload the file."
                            );
                            return;
                        }
                        setConfirmLoading(true);
                        try {
                            const confirmResult = await confirmFileUpload({
                                digest: fileDigest,
                            });

                            if (confirmResult.error || !confirmResult.data) {
                                setUploadError(
                                    confirmResult.error ||
                                        "Failed to confirm file upload"
                                );
                                return;
                            }

                            setUploadError(null);
                            testIdToUse = confirmResult.data.test_id;
                        } catch (error) {
                            setUploadError("Failed to confirm file upload");
                            return;
                        } finally {
                            setConfirmLoading(false);
                        }
                    }
                    if (!testIdToUse) {
                        alert("Please select a test");
                        return;
                    }
                    navigate(`/test/${testIdToUse}/data-input`);
                }}
            />

            <FileUploadProcessingDialog
                isOpen={uploadProcessing || uploadDialogError !== null}
                description={uploadStageDescription}
                error={uploadDialogError}
                onClose={handleCloseUploadDialog}
            />
        </div>
    );
}
